import React, { useState } from 'react';

interface Cell {
  row: number;
  col: number;
}

interface Cage {
  id: string;
  cells: Cell[];
  target: number;
  operation: string;
}

interface KenKenGridProps {
  size: number;
  selectedCells: Cell[];
  onCellSelection: (cells: Cell[]) => void;
  cages: Cage[];
  solution: number[][] | null;
}

const KenKenGrid: React.FC<KenKenGridProps> = ({ size, selectedCells, onCellSelection, cages, solution }) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [draggedCells, setDraggedCells] = useState<Cell[]>([]);

  const handleMouseDown = (row: number, col: number) => {
    setIsDragging(true);
    setDraggedCells([{ row, col }]);
  };

  const handleMouseOver = (row: number, col: number) => {
    if (isDragging) {
      const newCell: Cell = { row, col };
      if (!draggedCells.some(cell => cell.row === newCell.row && cell.col === newCell.col)) {
        setDraggedCells((prev) => [...prev, newCell]);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (draggedCells.length > 0) {
      onCellSelection(draggedCells);
      setDraggedCells([]); // Clear dragged cells after selection is finalized
    }
  };

  // Touch event handlers
  const handleTouchStart = (row: number, col: number) => {
    setIsDragging(true);
    setDraggedCells([{ row, col }]);
  };

  const handleTouchMove = (event: React.TouchEvent, row: number, col: number) => {
    if (isDragging) {
      event.preventDefault(); // Prevent scrolling while dragging
      const touch = event.touches[0];
      const targetElement = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null; // Cast to HTMLElement
      if (targetElement && targetElement.dataset.row && targetElement.dataset.col) {
        const newCell: Cell = { row: Number(targetElement.dataset.row), col: Number(targetElement.dataset.col) };
        if (!draggedCells.some(cell => cell.row === newCell.row && cell.col === newCell.col)) {
          setDraggedCells((prev) => [...prev, newCell]);
        }
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (draggedCells.length > 0) {
      onCellSelection(draggedCells);
      setDraggedCells([]); // Clear dragged cells after selection is finalized
    }
  };


  const isCellSelected = (row: number, col: number) => {
    return selectedCells.some((cell) => cell.row === row && cell.col === col);
  };

  const isCellDragged = (row: number, col: number) => {
    return draggedCells.some((cell) => cell.row === row && cell.col === col);
  };

  const getCageForCell = (row: number, col: number) => {
    return cages.find(cage =>
      cage.cells.some(cell => cell.row === row && cell.col === col)
    );
  };

  const getCellBorders = (row: number, col: number) => {
    const borders = {
      borderTop: '1px solid #dee2e6',
      borderRight: '1px solid #dee2e6',
      borderBottom: '1px solid #dee2e6',
      borderLeft: '1px solid #dee2e6',
    };

    const currentCellCage = getCageForCell(row, col);

    if (currentCellCage) {
      // Check top border
      const cellAbove: Cell = { row: row - 1, col };
      const cageAbove = getCageForCell(cellAbove.row, cellAbove.col);
      if (!cageAbove || cageAbove.id !== currentCellCage.id) {
        borders.borderTop = '2px solid black';
      }

      // Check right border
      const cellRight: Cell = { row, col: col + 1 };
      const cageRight = getCageForCell(cellRight.row, cellRight.col);
      if (!cageRight || cageRight.id !== currentCellCage.id) {
        borders.borderRight = '2px solid black';
      }

      // Check bottom border
      const cellBelow: Cell = { row: row + 1, col };
      const cageBelow = getCageForCell(cellBelow.row, cellBelow.col);
      if (!cageBelow || cageBelow.id !== currentCellCage.id) {
        borders.borderBottom = '2px solid black';
      }

      // Check left border
      const cellLeft: Cell = { row, col: col - 1 };
      const cageLeft = getCageForCell(cellLeft.row, cellLeft.col);
      if (!cageLeft || cageLeft.id !== currentCellCage.id) {
        borders.borderLeft = '2px solid black';
      }
    }

    // Grid outer borders
    if (row === 0) borders.borderTop = '2px solid black';
    if (col === size - 1) borders.borderRight = '2px solid black';
    if (row === size - 1) borders.borderBottom = '2px solid black';
    if (col === 0) borders.borderLeft = '2px solid black';

    return borders;
  };

  const cells = Array.from({ length: size * size }, (_, i) => {
    const row = Math.floor(i / size);
    const col = i % size;
    return { row, col };
  });

  return (
    <div
      className="d-flex flex-wrap"
      style={{
        width: `${size * 50}px`, // Assuming 50px per cell
        height: `${size * 50}px`,
      }}
      onMouseLeave={handleMouseUp} // End drag if mouse leaves grid
      onMouseUp={handleMouseUp}
      onTouchEnd={handleTouchEnd}
    >
      {cells.map((cell, index) => {
        const cage = getCageForCell(cell.row, cell.col);
        const isTopLeftOfCage = cage && cage.cells[0].row === cell.row && cage.cells[0].col === cell.col;
        const cellBorders = getCellBorders(cell.row, cell.col);
        const solutionValue = solution ? solution[cell.row][cell.col] : null;

        return (
          <div
            key={`${cell.row}-${cell.col}`}
            className={`d-flex flex-column justify-content-center align-items-center position-relative ${
              isCellSelected(cell.row, cell.col) ? 'bg-info' : ''
            } ${isCellDragged(cell.row, cell.col) ? 'bg-warning' : ''}`}
            style={{
              width: '50px',
              height: '50px',
              cursor: 'pointer',
              ...cellBorders,
            }}
            onMouseDown={() => handleMouseDown(cell.row, cell.col)}
            onMouseOver={() => handleMouseOver(cell.row, cell.col)}
            onTouchStart={() => handleTouchStart(cell.row, cell.col)}
            onTouchMove={(e) => handleTouchMove(e, cell.row, cell.col)}
            data-row={cell.row} // Add data attributes for touchmove
            data-col={cell.col} // Add data attributes for touchmove
          >
            {isTopLeftOfCage && (
              <div className="position-absolute top-0 start-0 p-1" style={{ fontSize: '0.7em' }}>
                {cage.cells.length === 1 ? cage.target : `${cage.target} ${cage.operation}`}
              </div>
            )}
            <span style={{ fontSize: '1.5em' }}>
              {solutionValue}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default KenKenGrid;